<template>
  <Modal
    class="scoped"
    full-height
    :modal-style="{ maxWidth: '900px' }"
    title="Bulk Apply Teams"
  >
    <div class="card q-pa-md no-wrap full-height scroll">
      <div class="q-pa-md q-gutter-md">
        <div>
          <QSpinner
            v-if="loading"
            class="full-width q-mt-lg"
            :size="30"
          />
          <h4>Team</h4>
          <SelectList
            v-model="selectedTeamId"
            clearable
            :disabled="loading"
            emit-value
            label="Select Team"
            option-label="name"
            :options="teams"
            @update:model-value="updateSelectedUsers"
          />
        </div>
        <div class="q-gutter-sm">
          <h4>Set as</h4>
          <QRadio
            v-model="teamType"
            label="Primary Team"
            val="primary"
          />
          <QRadio
            v-show="!isSelectedOperationalTeam"
            v-model="teamType"
            label="Additional Teams"
            val="additional"
          />
          <QRadio
            v-show="showOperationalTeamType"
            v-model="teamType"
            label="Operational Team"
            val="operational"
          />
        </div>
        <QSeparator />
        <div>
          <h4>Select Users</h4>
          <QList>
            <QItemLabel header>
              <QItemSection>
                <QInput
                  v-model="filter"
                  dense
                  filled
                  placeholder="Filter by Name"
                  square
                />
              </QItemSection>
            </QItemLabel>
            <div class="row">
              <QItem
                v-for="user in filteredUsers"
                :key="user.id"
                class="col-md-3 col-sm-6"
              >
                <QItemSection
                  side
                  top
                >
                  <QCheckbox
                    v-model="user.selected"
                    @update:model-value="updateUserSelection(user)"
                  />
                </QItemSection>
                <QItemSection>
                  <QItemLabel>
                    {{ user.label }}
                  </QItemLabel>
                </QItemSection>
              </QItem>
            </div>
          </QList>
        </div>
      </div>
    </div>

    <template #buttons>
      <BaseButton
        color="primary"
        label="Save"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import { sortBy } from "lodash-es";
import { storeToRefs } from "pinia";
import { Notify } from "quasar";
import { useUserStore } from "stores/user";

import BaseButton from "shared/components/base/BaseButton.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { Organisation } from "shared/resources";

import SelectList from "src/components/common/SelectList.vue";
import UserService from "src/services/api/UserService";

export default {
  name: "BulkApplyTeamsModal",
  components: {
    SelectList,
    Modal,
    BaseButton,
  },
  props: {
    organisationId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ["confirm"],
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      selectedTeamId: "",
      filter: "",
      users: [],
      selectedUsers: [],
      teams: [],
      loading: false,
      teamType: "primary",
    };
  },
  computed: {
    usersFormatted() {
      return sortBy(
        this.users.map((user) => ({
          id: user.id,
          label: user.name,
          selected: Boolean(
            this.selectedUsers.find((userId) => userId === user.id)
          ),
        })),
        "label"
      );
    },
    filteredUsers() {
      return this.usersFormatted.filter((user) =>
        user.label.toLowerCase().includes((this.filter || "").toLowerCase())
      );
    },
    isSelectedOperationalTeam() {
      return this.selectedTeam?.operationalTeam;
    },
    showOperationalTeamType() {
      return this.organisation.admin && this.isSelectedOperationalTeam;
    },
    selectedTeam() {
      return this.teams.find((team) => team.id === this.selectedTeamId);
    },
    primarySelectedTeamUserIds() {
      return this.selectedTeam?.userIds?.map((id) => id.toString());
    },
    operationalSelectedTeamUserIds() {
      return this.selectedTeam?.operationalTeamUserIds?.map((id) =>
        id.toString()
      );
    },
    isTeamTypePrimary() {
      return this.teamType === "primary";
    },
    isTeamTypeAdditional() {
      return this.teamType === "additional";
    },
    isTeamTypeOperational() {
      return this.teamType === "operational";
    },
  },
  watch: {
    teamType() {
      if (this.selectedTeamId) {
        this.updateSelectedUsers();
      }
    },
    selectedTeamId() {
      if (this.showOperationalTeamType) {
        if (this.isTeamTypeAdditional) {
          this.teamType = "primary";
        }
      } else if (this.isTeamTypeOperational) {
        this.teamType = "primary";
      }
    },
  },
  mounted() {
    this.loadUsersAndTeams();
  },
  methods: {
    updateUserSelection(user) {
      if (user.selected) {
        this.selectedUsers.push(user.id);
      } else {
        const index = this.selectedUsers.indexOf(user.id);
        this.selectedUsers.splice(index, 1);
      }
    },
    async updatePrimaryTeams(users) {
      const removedUsers = this.users.filter(
        (user) =>
          this.primarySelectedTeamUserIds.includes(user.id) &&
          !this.selectedUsers.includes(user.id)
      );

      await Promise.all(
        users
          .map((user) => {
            Object.assign(user, { organisation_team_id: this.selectedTeamId });

            return UserService.team(user);
          })
          .concat(
            removedUsers.map((user) => {
              Object.assign(user, { organisation_team_id: null });

              return UserService.team(user);
            })
          )
      );
    },
    async assignUsersToTeam(users, userIdsKey) {
      this.selectedTeam[userIdsKey] = users.map((user) => user.id);

      await this.selectedTeam.saveResource();
    },
    async updateTeam(users) {
      if (this.isTeamTypePrimary) {
        await this.updatePrimaryTeams(users);
      } else if (this.isTeamTypeAdditional) {
        await this.assignUsersToTeam(users, "secondaryUserIds");
      } else if (this.isTeamTypeOperational) {
        await this.assignUsersToTeam(users, "operationalTeamUserIds");
      }
    },
    async save(cancel) {
      this.loading = true;

      try {
        const selectedUsers = this.users.filter((user) =>
          this.selectedUsers.includes(user.id)
        );

        await this.updateTeam(selectedUsers);
        Notify.create("Users updated successfully");
      } catch (error) {
        Notify.create(
          `There was an error updating the user.: ${error.message}`
        );

        cancel();
      } finally {
        this.loading = false;
        this.$emit("confirm");
      }
    },
    updateSelectedUsers() {
      if (this.isTeamTypePrimary) {
        this.selectedUsers = [...this.primarySelectedTeamUserIds];
      } else if (this.isTeamTypeAdditional) {
        this.selectedUsers = this.selectedTeam.secondaryUserIds.map((id) =>
          id.toString()
        );
      } else if (this.isTeamTypeOperational) {
        this.selectedUsers = [...this.operationalSelectedTeamUserIds];
      }
    },
    async loadUsersAndTeams() {
      this.loading = true;

      try {
        const organisation = (
          await Organisation.includes(["organisationTeams", "users"])
            .where({ users: { active: true } })
            .select({
              organisations: ["id"],
              users: ["name"],
              organisationTeams: [
                "name",
                "secondaryUserIds",
                "userIds",
                "operationalTeam",
                "operationalTeamUserIds",
              ],
            })
            .find(this.organisationId)
        ).data;

        this.teams = organisation.organisationTeams;
        this.users = organisation.users;
      } catch (error) {
        this.$q.notify(
          `There was an error loading organisation teams & users: ${error}`
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
